import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useAppState } from "../../hooks/useAppState";
import { getVanillaEl } from "../../domBridge";
import { onPresenceChanged } from "../../../chatSocket";
import { applyPresenceChanged } from "../../../presenceApi";
import { AvatarImage } from "../../components/AvatarImage";

// Small avatar-with-dot per other directory user (normally just one, the
// common 2-person case), portaled next to the brand badge/logo. Online
// state comes from server.js's chatSockets map - the same per-user
// WebSocket connection chat/Together Mode already track, not a separate
// presence mechanism - so this is accurate the instant their last tab
// disconnects, not on some polling delay (the 120s protected-features
// poll in AuthGate.tsx is just a fallback if a push is ever missed).
//
// Hidden on Home ("/") specifically - the Couple Header already shows both
// avatars there, so this mini header copy would be a redundant second
// profile representation. useLocation (not appState) so a hard refresh on
// any route resolves visibility correctly from the very first render, not
// just after a subsequent in-app navigation.
export function PresenceIndicator() {
  const presenceUsers = useAppState((s) => s.presenceUsers);
  const { pathname } = useLocation();
  const anchor = getVanillaEl<HTMLDivElement>("presenceAnchor");

  useEffect(() => onPresenceChanged((data) => applyPresenceChanged(data)), []);

  if (!anchor || !presenceUsers.length || pathname === "/") return null;

  return createPortal(
    <div className="d-flex align-items-center gap-1">
      {presenceUsers.map((u) => {
        const label = (u.fullName || u.email || "?").trim().charAt(0).toUpperCase();
        const displayName = u.fullName || u.email;
        return (
          <div
            key={u.id}
            title={`${displayName} is ${u.online ? "online" : "offline"}`}
            aria-label={`${displayName} is ${u.online ? "online" : "offline"}`}
            style={{
              position: "relative",
              width: 28,
              height: 28,
              flexShrink: 0,
              borderRadius: "50%",
              background: "var(--pink-500)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.75rem",
              fontWeight: 600,
            }}
          >
            {u.avatarFilename ? (
              <AvatarImage
                filename={u.avatarFilename}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
                fallback={label}
              />
            ) : (
              label
            )}
            {u.online && (
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  bottom: -1,
                  right: -1,
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: "#2ecc71",
                  border: "2px solid var(--body-bg-solid)",
                }}
              />
            )}
          </div>
        );
      })}
    </div>,
    anchor,
  );
}
